/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';

import { Form, FormRenderProps } from 'react-final-form';

import { FormApi, SubmissionErrors } from 'final-form';

import Typography from '@material-ui/core/Typography';

import Skeleton from '@material-ui/lab/Skeleton';

import arrayMutators, { DefaultType } from 'final-form-arrays';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import IDtoClass from '../../utilities/IDtoClass';


import * as Dtos from '../../api/Neon.Dtos';



import { RequestState } from '@ngt/request-utilities';

import ValidationContext, { DEFAULT_VALIDATION, ValidationLoadStateContext } from '../../context/ValidationContext';


import FormTypeContext from '../../context/FormTypeContext';

import Button from '@material-ui/core/Button';
import { EventContext, EventTypeContext, FormContext, IForm, onlinePatientManagement, FormTypeContext as OpmsFormTypeContext, LookupsContext as OpmsLookupsContext, PatientContext as OpmsPatientContext, patientApi } from '@ngt/online-patient-management-ui';
import { useSnackbar } from 'notistack';
import { Link, useHistory, useParams } from 'react-router-dom';
import DisableFormContext from '../../context/DisableFormContext';
import LookupsContext from '../../context/LookupsContext';
import PatientContext from '../../context/PatientContext';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */
interface IOpmsFormProps<Type extends IForm | null> {
    institutionId: number | null;
    patientId: number | null;
    eventTypeId: number | null;
    eventId: number | null;
    parentFormTypeId: number | null;
    parentFormId: number | null;
    formRepeat: number;
    readyToLoad: boolean;

    formClass: IDtoClass<Type>;
    children?: React.ReactNode;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    heading: {
        padding: theme.spacing(3, 3, 2, 3)
    }
}));

const useFormStyles = makeStyles<Theme>(theme => ({
    buttonSet: {
        textAlign: 'right',
        padding: theme.spacing(3)
    },
    button: {
        marginLeft: theme.spacing(3),

        '&:first-child': {
            marginLeft: theme.spacing(0)
        }
    }
}));


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const InternalForm = <Type extends IForm | null>({
    form,
    values,
    handleSubmit,
    submitting,
    children
}: FormRenderProps<Type>) => {

    const [
        loadedForm,
        validation,
        ,
        saveState,
        ,
        validationState,
        formActions
    ] = React.useContext(FormContext);

    const { enqueueSnackbar } = useSnackbar();

    const classes = useFormStyles();

    const timerRef = React.useRef<number>();

    const patient = React.useContext(PatientContext);

    const [formType] = React.useContext(OpmsFormTypeContext);

    const [eventType] = React.useContext(EventTypeContext);

    const [event] = React.useContext(EventContext);

    const params = useParams<any>();
    const history = useHistory();

    const [redirect, setRedirect] = React.useState(false);

    const validating = !validationState || validationState.state === RequestState.Pending || validationState.state === RequestState.None;

    React.useEffect(() => {
        const groupErrors = validation?.errors?.reduce((a, b) => {
                const propertyName = b.property

                if (!a[propertyName]) {
                    a[propertyName] = [];
                }

                a[propertyName].push(b);

                return a;
            }, {}) ?? {};

        form.mutators.setErrors(groupErrors);
        form.mutators.setValid(validation?.result === Dtos.ResultType.Valid);
    }, [validation, form.mutators])

    React.useEffect(() => {
        window.clearTimeout(timerRef.current);

        if (!validating) {
            form.mutators.setValidating(validating);
        }
        else {
            timerRef.current = window.setTimeout(() => {
                form.mutators.setValidating(validating);
            }, 500);
        }
    }, [validating, form.mutators.setValidating, form.mutators])

    React.useEffect(() => {
        if (formActions?.validate) {
            formActions?.validate(values as any);
        }
    }, [values, formActions?.validate, formActions])

    React.useEffect(() => {
        if (saveState.state === RequestState.Success || saveState.state === RequestState.Failure) {
            form.mutators.setSubmitting(false);
        }

        if (saveState.state === RequestState.Success) {
            // console.log('spam');
            let variant = 'success';
            let message = 'The form was successfully saved!'

            switch (validation?.result)
            {
                case Dtos.ResultType.StratificationFailure:
                    variant = 'error';
                    message = 'The form was successfully saved but there is an issue with the data required for stratification.';
                    break;
                case Dtos.ResultType.Error:
                    variant = 'error';
                    message = 'The form was successfully saved but some data is invalid.';
                    break;
                case Dtos.ResultType.Ineligible:
                    variant = 'warning';
                    message = 'The form was successfully saved but results in the patient being ineligible.';
                    break;
            }

            enqueueSnackbar(message, { variant: variant as any, key: 'form-saved' });

            if (redirect) {
                if (patient?.id) {
                    history.push(`/registration/${params[onlinePatientManagement.routeParameters.countryCode]}/${params[onlinePatientManagement.routeParameters.institutionCode]}/${patient.studyNumber}`)
                }
                else if (loadedForm?.patientId) {
                    patientApi.loadById(loadedForm.patientId).then(response => {
                        history.push(`/registration/${params[onlinePatientManagement.routeParameters.countryCode]}/${params[onlinePatientManagement.routeParameters.institutionCode]}/${response.patient.studyNumber}`)
                    });
                }
                else {
                    history.push(`/registration/${params[onlinePatientManagement.routeParameters.countryCode]}/${params[onlinePatientManagement.routeParameters.institutionCode]}`);
                }
            }
            else if (!patient?.id) {
                if (loadedForm?.patientId) {
                    patientApi.loadById(loadedForm.patientId).then(response => {
                        history.push(`/registration/${params[onlinePatientManagement.routeParameters.countryCode]}/${params[onlinePatientManagement.routeParameters.institutionCode]}/${response.patient.studyNumber}/${eventType?.code}/${event?.eventRepeat}/${formType?.code}/${loadedForm?.formRepeat}`)
                    });
                }
                else {
                    history.push(`/registration/${params[onlinePatientManagement.routeParameters.countryCode]}/${params[onlinePatientManagement.routeParameters.institutionCode]}`);
                }
            }
        }
    }, [saveState, form.mutators.setSubmitting, patient, redirect, history, loadedForm, formType, eventType, event, validation?.result, enqueueSnackbar, form.mutators, params])

    const submit = React.useCallback((e) => {
        form.mutators.markAllFieldsTouched(true);
        setRedirect(false);
        form.mutators.setSubmitting(true);
        formActions?.save(values as any)
    }, [values, setRedirect, form.mutators, formActions])

    const submitAndReturn = React.useCallback((e) => {
        form.mutators.markAllFieldsTouched(true);
        setRedirect(true);
        form.mutators.setSubmitting(true);
        formActions?.save(values as any)
    }, [values, setRedirect, form.mutators, formActions]);

    const formDisabled = React.useContext(DisableFormContext);

    return (
        <>
            {children}
            <div
                className={classes.buttonSet}
            >
                {
                    !formDisabled && (
                        <>
                            <Button
                                disabled={!validation || validation.result === Dtos.ResultType.Critical || validating || submitting}
                                variant="contained"
                                color="primary"
                                onClick={submit}
                                className={classes.button}
                            >
                                Submit Form
                            </Button>
                            <Button
                                disabled={!validation || validation.result === Dtos.ResultType.Critical || validating || submitting}
                                variant="contained"
                                color="primary"
                                onClick={submitAndReturn}
                                className={classes.button}
                            >
                                Submit Form And Return
                            </Button>
                        </>
                    )
                }
                <Button
                    disabled={submitting}
                    variant="contained"
                    color="primary"
                    component={Link}
                    to={
                        patient?.id ?
                            `/registration/${params[onlinePatientManagement.routeParameters.countryCode]}/${params[onlinePatientManagement.routeParameters.institutionCode]}/${patient.studyNumber}` :
                            `/registration/${params[onlinePatientManagement.routeParameters.countryCode]}/${params[onlinePatientManagement.routeParameters.institutionCode]}`
                    }
                    className={classes.button}
                >
                    {
                        formDisabled ?
                            'Back' :
                            'Cancel'
                    }
                </Button>
            </div>
        </>
    );
};

const OpmsForm = <Type extends IForm>({
    formClass,
    patientId,
    institutionId,
    eventTypeId,
    eventId,
    parentFormTypeId,
    parentFormId,
    formRepeat,
    children,
    readyToLoad

}: IOpmsFormProps<Type>) => {
    const [formType] = React.useContext(OpmsFormTypeContext);
    const [eventType] = React.useContext(EventTypeContext);
    const [patient] = React.useContext(OpmsPatientContext);

    const patientStatus = React.useMemo(() => {
        return patient?.status ?? Dtos.PatientStatuses.NewPatient
    }, [patient]);

    const write = React.useMemo(() => {
        return eventType?.formTypes?.find(ft => ft.formTypeId === formType?.id)?.states?.find(fts => fts.patientStatus === patientStatus)?.write;
    }, [eventType, formType, patientStatus]);

    const [
        form,
        validation,
        ,
        saveState,
        ,
        validationState, 
        formActions
    ] = React.useContext(FormContext);

    const [lookups, , ] = React.useContext(OpmsLookupsContext);

    const classes = useStyles();

    const onSubmit = React.useCallback((values: Type | null, finalForm: FormApi<Type | null>, callback?: (errors?: SubmissionErrors) => void) => {
        return formActions?.save(values as any);
    }, [formActions]);

    const internalForm = React.useCallback((props: FormRenderProps<Type | null>) => {
        return InternalForm({ ...props, children });
    }, [children]);

    const [skeletonWidth] = React.useState((Math.random() * 85) + 15);

    const patientCohortC = (patient as Dtos.Patient)?.treatmentId !== 1 && (patient as Dtos.Patient)?.treatmentId !== 2;

    const formName = formType?.code === 'pre-registration' && patientCohortC ? 'Screening Form' : formType?.name;


    return (
        <LookupsContext.Provider value={lookups}>
            <FormTypeContext.Provider value={formType}>
                <ValidationLoadStateContext.Provider value={validationState}>
                    <ValidationContext.Provider value={validation ?? DEFAULT_VALIDATION}>
                        <DisableFormContext.Provider value={!write || saveState.state === RequestState.Pending}>
                            <Form
                                initialValues={form as Type | null}
                                onSubmit={onSubmit}
                                render={internalForm}
                                // validate={validate}
                                subscription={{ values: true, valid: true, validating: true, submitting: true }}
                                mutators={{
                                    markAllFieldsTouched: (args: [boolean], state, tools) => {
                                        Object.keys(state.fields).forEach(key => {
                                            state.fields[key].touched = args[0]
                                        });
                                    },
                                    setErrors: (args: [Record<string, Dtos.IValidationError>], state, tools) => {
                                        state.formState.errors = args[0];
                                        // state.formState = tools.setIn(state.formState, 'errors', args[0]) as any;
                                    },
                                    setValid: (args: [boolean], state, tools) => {
                                        state.formState.valid = args[0];
                                    },
                                    setValidating: (args: [number], state, tools) => {
                                        state.formState.validating = args[0];
                                    },
                                    setSubmitting: (args: [boolean], state, tools) => {
                                        state.formState.submitting = args[0];
                                    },
                                    ...arrayMutators as DefaultType<Type | null>
                                }}
                            />
                        </DisableFormContext.Provider>
                    </ValidationContext.Provider>
                </ValidationLoadStateContext.Provider>
            </FormTypeContext.Provider>
        </LookupsContext.Provider>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default OpmsForm;
